import React from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { theme } from "./theme";

export default function CartDrawer({ open, onClose }) {
  const { cart, removeFromCart, updateQty, subtotal, totalItems } = useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          <Overlay
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Drawer
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
          >
            <Header>
              <Title>Shporta ({totalItems})</Title>
              <CloseBtn onClick={onClose}>✕</CloseBtn>
            </Header>

            <Items>
              {cart.length === 0 && (
                <Empty>Shporta juaj është bosh.</Empty>
              )}
              {cart.map((item) => (
                <Item key={`${item.id}_${item.variant}`}>
                  <ItemImg src={item.imageUrl} alt={item.name} />
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemVariant>{item.variant} shots</ItemVariant>
                    <ItemPrice>€{(item.price * item.qty).toFixed(2)}</ItemPrice>
                    <QtyRow>
                      <QtyBtn onClick={() => item.qty > 1 ? updateQty(item.id, item.variant, item.qty - 1) : removeFromCart(item.id, item.variant)}>−</QtyBtn>
                      <QtyNum>{item.qty}</QtyNum>
                      <QtyBtn onClick={() => updateQty(item.id, item.variant, item.qty + 1)}>+</QtyBtn>
                      <RemoveBtn onClick={() => removeFromCart(item.id, item.variant)}>Fshi</RemoveBtn>
                    </QtyRow>
                  </ItemInfo>
                </Item>
              ))}
            </Items>

            {cart.length > 0 && (
              <Footer>
                <SubtotalRow>
                  <span>Nëntotali</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </SubtotalRow>
                <CheckoutBtn as={Link} to="/checkout" onClick={onClose}>
                  Vazhdo me Porosinë →
                </CheckoutBtn>
              </Footer>
            )}
          </Drawer>
        </>
      )}
    </AnimatePresence>
  );
}

const Overlay = styled(motion.div)`
  position: fixed; inset: 0; background: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px); z-index: 200;
`;
const Drawer = styled(motion.div)`
  position: fixed; top: 0; right: 0; bottom: 0; width: 420px; max-width: 95vw;
  background: ${theme.colors.bgCard}; z-index: 201;
  display: flex; flex-direction: column;
  border-left: 1px solid ${theme.colors.borderSubtle};
`;
const Header = styled.div`
  padding: 1.5rem; display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid ${theme.colors.borderSubtle};
`;
const Title = styled.h2`
  font-family: 'Playfair Display', serif; font-size: 1.3rem; font-style: italic;
  color: ${theme.colors.textPrimary}; margin: 0;
`;
const CloseBtn = styled.button`
  background: none; border: none; color: ${theme.colors.textSecondary};
  font-size: 1.2rem; cursor: pointer; padding: 4px;
  &:hover { color: ${theme.colors.textPrimary}; }
`;
const Items = styled.div`
  flex: 1; overflow-y: auto; padding: 1rem 1.5rem;
  display: flex; flex-direction: column; gap: 1rem;
`;
const Empty = styled.p`
  color: ${theme.colors.textMuted}; font-size: 0.9rem; text-align: center;
  margin-top: 2rem;
`;
const Item = styled.div`
  display: flex; gap: 1rem; align-items: flex-start;
  padding-bottom: 1rem; border-bottom: 1px solid ${theme.colors.borderSubtle};
`;
const ItemImg = styled.img`
  width: 72px; height: 72px; object-fit: cover;
  border-radius: ${theme.radius.md}; flex-shrink: 0;
`;
const ItemInfo = styled.div`display: flex; flex-direction: column; gap: 2px; flex: 1;`;
const ItemName = styled.span`
  font-size: 0.9rem; font-weight: 500; color: ${theme.colors.textPrimary};
`;
const ItemVariant = styled.span`
  font-size: 0.75rem; color: ${theme.colors.textMuted};
  font-family: ${theme.fonts.mono};
`;
const ItemPrice = styled.span`
  font-size: 0.85rem; color: #89d146;
  font-family: ${theme.fonts.mono};
`;
const QtyRow = styled.div`display: flex; align-items: center; gap: 8px; margin-top: 6px;`;
const QtyBtn = styled.button`
  width: 26px; height: 26px; border-radius: 50%;
  border: 1px solid ${theme.colors.border}; background: none;
  color: ${theme.colors.textSecondary}; font-size: 1rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: ${theme.colors.bgSurface}; color: ${theme.colors.textPrimary}; }
`;
const QtyNum = styled.span`
  font-size: 0.85rem; color: ${theme.colors.textPrimary}; min-width: 20px; text-align: center;
`;
const RemoveBtn = styled.button`
  background: none; border: none; color: ${theme.colors.textMuted};
  font-size: 0.7rem; cursor: pointer; font-family: ${theme.fonts.mono};
  text-transform: uppercase; letter-spacing: 0.06em; margin-left: 4px;
  &:hover { color: #e05c5c; }
`;
const Footer = styled.div`
  padding: 1.5rem; border-top: 1px solid ${theme.colors.borderSubtle};
`;
const SubtotalRow = styled.div`
  display: flex; justify-content: space-between;
  color: ${theme.colors.textSecondary}; font-size: 0.9rem; margin-bottom: 1rem;
`;
const CheckoutBtn = styled.button`
  display: block; width: 100%; padding: 1rem;
  background: #89d146; color: white; border: none; border-radius: 999px;
  font-family: ${theme.fonts.body}; font-size: 0.9rem; font-weight: 600;
  letter-spacing: 0.06em; cursor: pointer; text-align: center; text-decoration: none;
  transition: background 0.25s ease;
  &:hover { background: #7bc23e; }
`;
